import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { saveBlobResponse } from '../utils/download';
import ItemImport from './ItemImport';

import DateInput from './DateInput';
const STATUS_BADGE = { NEW: 'success', DUPLICATE: 'secondary', INVALID: 'danger' };
const STATUS_LABEL = { NEW: 'Will import', DUPLICATE: 'Already exists', INVALID: 'Needs fixing' };
const MAX_ROWS_SHOWN = 300;

const errorText = async (error, fallback) => {
  let data = error.response?.data;
  if (data instanceof Blob) {
    data = await data.text();
  }
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Org Admin and Accounts tab: Tally export, customer/vendor import, and item import, from a Tally masters file or CSV. The finance
 * roles get the first two; importing items (canImportItems) stays with the Org Admin.
 */
function DataImportExport({ canImportItems = true }) {
  const fileInput = useRef(null);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [billWise, setBillWise] = useState(false);

  const [format, setFormat] = useState('TALLY');
  const [file, setFile] = useState(null);
  const [includeCustomers, setIncludeCustomers] = useState(true);
  const [includeVendors, setIncludeVendors] = useState(true);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [vendorTypeId, setVendorTypeId] = useState('');
  const [fillMissing, setFillMissing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get('/api/inventory/vendor-types/active')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setVendorTypes(list);
        if (list.length === 1) setVendorTypeId(String(list[0].id));
      })
      .catch(() => {});
  }, []);

  const vendorsInvolved = format === 'VENDORS_CSV' || (format === 'TALLY' && includeVendors);

  const resetPreview = () => {
    setPreview(null);
    setDone(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {};
      if (exportFrom) params.from = exportFrom;
      if (exportTo) params.to = exportTo;
      if (billWise) params.billWise = true;
      const response = await axios.get('/api/accounts/tally/export', { params, responseType: 'blob' });
      saveBlobResponse(response, 'tally-export.zip');
      toast.success('Export downloaded');
    } catch (error) {
      toast.error(await errorText(error, 'Failed to export'));
    } finally {
      setExporting(false);
    }
  };

  const handleTemplate = async (type) => {
    try {
      const response = await axios.get(`/api/data-import/templates/${type}.csv`, { responseType: 'blob' });
      saveBlobResponse(response, `${type}-template.csv`);
    } catch (error) {
      toast.error('Failed to download the template');
    }
  };

  const send = async (action) => {
    if (!file) {
      toast.error('Choose a file first');
      return null;
    }
    const body = new FormData();
    body.append('file', file);
    body.append('format', format);
    body.append('includeCustomers', format === 'VENDORS_CSV' ? 'false' : String(format === 'TALLY' ? includeCustomers : true));
    body.append('includeVendors', format === 'CUSTOMERS_CSV' ? 'false' : String(format === 'TALLY' ? includeVendors : true));
    if (vendorsInvolved && vendorTypeId) body.append('vendorTypeId', vendorTypeId);
    body.append('fillMissingVendorFields', String(fillMissing));
    const response = await axios.post(`/api/data-import/parties/${action}`, body);
    return response.data;
  };

  const handlePreview = async () => {
    try {
      setBusy(true);
      setDone(null);
      const result = await send('preview');
      if (result) setPreview(result);
    } catch (error) {
      setPreview(null);
      toast.error(await errorText(error, 'Failed to read the file'));
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    try {
      setBusy(true);
      const result = await send('commit');
      if (result) {
        setDone(result);
        setPreview(null);
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        toast.success(`Imported ${result.newCount} record${result.newCount === 1 ? '' : 's'}`);
      }
    } catch (error) {
      toast.error(await errorText(error, 'Import failed - nothing was imported'));
    } finally {
      setBusy(false);
    }
  };

  const shownRows = preview ? preview.rows.slice(0, MAX_ROWS_SHOWN) : [];

  return (
    <Row className="g-4">
      <Col lg={12}>
        <Card>
          <Card.Header><h5 className="mb-0">Export to Tally</h5></Card.Header>
          <Card.Body>
            <p className="text-muted">
              Downloads a zip with your customers, vendors, bank and cash accounts, sales and GST ledgers, stock items,
              and the sales, receipt, payment and purchase vouchers in the chosen period. In Tally, import
              <strong> 1-masters.xml </strong> first, then <strong>2-vouchers.xml</strong>. A README inside the zip lists what is
              and isn&apos;t included.
            </p>
            <Alert variant="warning" className="py-2 small">
              Try the first import on a test company in Tally, and have your accountant check the result before using it for real books.
            </Alert>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Vouchers from</Form.Label>
                  <DateInput value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Vouchers to</Form.Label>
                  <DateInput value={exportTo} onChange={(e) => setExportTo(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md="auto">
                <Button variant="primary" onClick={handleExport} disabled={exporting}>
                  {exporting ? 'Preparing...' : 'Download Tally files (.zip)'}
                </Button>
              </Col>
            </Row>
            <Form.Text className="text-muted">
              Leave the dates blank to export everything. Opening balances come from your ledgers, so export the full history when moving your books.
            </Form.Text>
            <Form.Check
              className="mt-3"
              type="checkbox"
              id="tally-bill-wise"
              checked={billWise}
              onChange={(e) => setBillWise(e.target.checked)}
              label="Match receipts and credit notes to invoices (bill-wise tracking for customers)"
            />
            <Form.Text className="text-muted d-block">
              Turns on bill-wise tracking for customers in Tally, so a credit note or receipt settles the invoice it was applied to
              instead of sitting on the customer&apos;s account. Anything not applied to an invoice in this export stays on account.
              Leave this off if your accountant already keeps these ledgers differently.
            </Form.Text>
          </Card.Body>
        </Card>
      </Col>

      <Col lg={12}>
        <Card>
          <Card.Header><h5 className="mb-0">Import customers and vendors</h5></Card.Header>
          <Card.Body>
            <p className="text-muted">
              Bring parties in from a Tally masters export (in Tally: Export &gt; Masters) or a CSV. You see a preview first, and
              nothing is saved until you confirm. Existing customers and vendors are never changed; matches are skipped.
            </p>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>File type</Form.Label>
                  <Form.Select value={format} onChange={(e) => { setFormat(e.target.value); resetPreview(); }}>
                    <option value="TALLY">Tally masters export (XML)</option>
                    <option value="CUSTOMERS_CSV">Customers (CSV)</option>
                    <option value="VENDORS_CSV">Vendors (CSV)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>File</Form.Label>
                  <Form.Control
                    ref={fileInput}
                    type="file"
                    accept={format === 'TALLY' ? '.xml' : '.csv'}
                    onChange={(e) => { setFile(e.target.files[0] || null); resetPreview(); }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {format !== 'TALLY' && (
              <p className="small mt-2 mb-0">
                Need the column layout?{' '}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => handleTemplate(format === 'VENDORS_CSV' ? 'vendors' : 'customers')}>
                  Download the {format === 'VENDORS_CSV' ? 'vendors' : 'customers'} template
                </Button>
              </p>
            )}

            {format === 'TALLY' && (
              <div className="mt-3 d-flex gap-4">
                <Form.Check type="checkbox" id="imp-customers" label="Customers (Sundry Debtors)"
                  checked={includeCustomers} onChange={(e) => { setIncludeCustomers(e.target.checked); resetPreview(); }} />
                <Form.Check type="checkbox" id="imp-vendors" label="Vendors (Sundry Creditors)"
                  checked={includeVendors} onChange={(e) => { setIncludeVendors(e.target.checked); resetPreview(); }} />
              </div>
            )}

            {vendorsInvolved && (
              <Row className="g-3 mt-1">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Vendor type for imported vendors</Form.Label>
                    <Form.Select value={vendorTypeId} onChange={(e) => { setVendorTypeId(e.target.value); resetPreview(); }}>
                      <option value="">-- Choose --</option>
                      {vendorTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={8} className="d-flex align-items-end">
                  <Form.Check type="checkbox" id="imp-fill" label="Fill missing vendor phone/city with N/A (vendors require them)"
                    checked={fillMissing} onChange={(e) => { setFillMissing(e.target.checked); resetPreview(); }} />
                </Col>
              </Row>
            )}

            <div className="mt-3">
              <Button variant="outline-primary" onClick={handlePreview} disabled={busy || !file}>
                {busy && !preview ? <Spinner animation="border" size="sm" /> : 'Preview'}
              </Button>
            </div>

            {done && (
              <Alert variant="success" className="mt-3 mb-0">
                Imported {done.newCount} new record{done.newCount === 1 ? '' : 's'}.
                {done.duplicateCount > 0 && ` ${done.duplicateCount} already ${done.duplicateCount === 1 ? 'existed and was' : 'existed and were'} skipped.`}
                {done.invalidCount > 0 && ` ${done.invalidCount} ${done.invalidCount === 1 ? 'was' : 'were'} not imported because ${done.invalidCount === 1 ? 'it needs' : 'they need'} fixing.`}
              </Alert>
            )}

            {preview && (
              <div className="mt-4">
                <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                  <Badge bg="success">{preview.newCount} will import</Badge>
                  <Badge bg="secondary">{preview.duplicateCount} already exist</Badge>
                  <Badge bg="danger">{preview.invalidCount} need fixing</Badge>
                  <span className="text-muted small">{preview.total} rows read</span>
                  <Button variant="primary" className="ms-auto" onClick={handleCommit} disabled={busy || preview.newCount === 0}>
                    {busy ? 'Importing...' : `Import ${preview.newCount} new`}
                  </Button>
                </div>
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr><th>Line</th><th>Type</th><th>Name</th><th>Result</th><th>Details</th></tr>
                  </thead>
                  <tbody>
                    {shownRows.map((row, i) => (
                      <tr key={`${row.line}-${i}`}>
                        <td>{row.line}</td>
                        <td className="text-capitalize">{row.kind}</td>
                        <td>{row.name}</td>
                        <td><Badge bg={STATUS_BADGE[row.status] || 'secondary'}>{STATUS_LABEL[row.status] || row.status}</Badge></td>
                        <td className="small">
                          {row.message}
                          {(row.warnings || []).map((w) => <div key={w} className="text-warning">{w}</div>)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {preview.rows.length > MAX_ROWS_SHOWN && (
                  <p className="text-muted small">Showing the first {MAX_ROWS_SHOWN} of {preview.rows.length} rows; the import covers all of them.</p>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
      {canImportItems && (
        <Col lg={12}>
          <ItemImport />
        </Col>
      )}
    </Row>
  );
}

export default DataImportExport;
